// ===========================================
// The admin's view of a Meet & Greet's notes (#255, enquiries Phase 4)
// ===========================================
// Mounted on the Meet & Greet detail and in the enquiry panel's Meet & Greet block. Note text is the
// Meet & Greet permission's and no other (P45): the notes query AND the booking read are gated on
// `meet_and_greet.read`, so a viewer with only `enquiries.read` fires neither and sees nothing here.
// "Record notes" needs `meet_and_greet.write` and a recordable booking.

import { useState } from 'react';
import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { useMeetAndGreet } from '../../hooks/useMeetAndGreet';
import { useMeetAndGreetNotes } from '../../hooks/useMeetAndGreetNotes';
import { adminMayRecordNotes } from '../../utils/meetAndGreetNotes';
import MeetAndGreetNotesList, { NotesMissingChip } from './MeetAndGreetNotesList';
import RecordNotesDialog from './RecordNotesDialog';

interface MeetAndGreetNotesSectionProps {
  meetAndGreetId: string;
  childName: string;
}

export default function MeetAndGreetNotesSection({ meetAndGreetId, childName }: MeetAndGreetNotesSectionProps) {
  const { can } = usePermissions();
  const canRead = can('meet_and_greet', 'read');
  const canWrite = can('meet_and_greet', 'write');
  const [recordOpen, setRecordOpen] = useState(false);

  // Both reads need the permission; the booking carries the live `notesDueAt` / time / status.
  const booking = useMeetAndGreet(meetAndGreetId, canRead);
  const notes = useMeetAndGreetNotes(meetAndGreetId, canRead);

  if (!canRead) return null;

  const missing = booking.data?.notesMissing === true;
  const recordable = canWrite && adminMayRecordNotes(booking.data);

  return (
    <Box data-testid="mng-notes-section">
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Teacher notes
          </Typography>
          {missing && <NotesMissingChip />}
        </Stack>
        {recordable && (
          <Button size="small" variant="outlined" onClick={() => setRecordOpen(true)}>
            Record notes
          </Button>
        )}
      </Stack>

      {booking.isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load the Meet &amp; Greet, so notes can&apos;t be recorded right now.
        </Alert>
      )}

      {notes.isLoading ? (
        <Skeleton variant="rectangular" height={64} />
      ) : notes.isError ? (
        <Alert severity="error">Failed to load the notes.</Alert>
      ) : (notes.data ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No notes recorded yet.
        </Typography>
      ) : (
        <MeetAndGreetNotesList notes={notes.data ?? []} />
      )}

      {recordable && (
        <RecordNotesDialog
          open={recordOpen}
          onClose={() => setRecordOpen(false)}
          meetAndGreetId={meetAndGreetId}
          mode="admin"
          childName={childName}
        />
      )}
    </Box>
  );
}
